/**
 * Internationalization message catalogues.
 *
 * Supported locales:
 * - en-US: American English (default)
 * - en-ZA: South African English
 * - af-ZA: Afrikaans
 */
export const messages = {
    'en-US': {
        accountCreateFailed: 'Failed to create account. Please fix errors and try again.',
        accountCreatedSuccess: 'Account created successfully!',
        bookingBackHome: 'Back to Home',
        bookingSuccessHeading: 'Booking Successful',
        bookingSuccessText: 'Your tee time has been confirmed. A confirmation email will arrive shortly.',
        createAction: 'Create',
        createError: 'An error occurred while creating the price schedule. Please try again.',
        dontHaveAccount: "Don't have an account?",
        emailAlreadyExists: 'An account already exists for this email. Log in to continue with TeeTime, or reset your password to recover access.',
        emailAlreadyExistsLogin: 'Log in with this email',
        emailAlreadyExistsResetPassword: 'Reset password',
        existingIdentityConfirmation: 'We have you in our system. Please confirm this is you and complete your TeeTime registration.',
        existingIdentityConfirm: 'Confirm and continue',
        existingIdentityConfirmTitle: 'Confirm your existing identity',
        existingIdentityNextStep: 'You will sign in with this email, then TeeTime will finish linking player access.',
        forgotPassword: 'Forgot Password?',
        forgotPasswordHeading: 'Reset your password',
        forgotPasswordDescription: 'Enter the email address associated with your account and we will send you a reset link.',
        forgotPasswordDisabledMessage: 'Password reset is currently unavailable. Please contact support.',
        forgotPasswordSubmit: 'Send reset link',
        forgotPasswordSuccess: 'If the email exists, we have sent password reset instructions.',
        forgotPasswordInvalidEmail: 'Enter a valid email address',
        forgotPasswordEmailLabel: 'Email address',
        forgotPasswordEmailPlaceholder: 'you@example.com',
        forgotPasswordRemembered: 'Remembered your password?',
        genericError: 'An unexpected error occurred. Please try again.',
        goToHome: 'Go to Home',
        goToLogin: 'Go to Login',
        backButton: 'Back',
        invalidPhoneNumber: 'Phone number is invalid or not a mobile number.',
        invalidPhoneNumberField: 'Invalid phone number',
        loginButton: 'Log In',
        loginCheckError: 'Login failed. Check your email/password and try again.',
        loginError: 'Login failed. Please try again.',
        loginFailedTitle: 'Login Failed',
        loginHeading: 'Log In',
        loginSuccess: 'Login successful!',
        loginWelcome: 'Welcome to {appName}',
        loginMfaInstruction: 'Enter the verification code we just sent to finish signing in.',
        loginMfaMissingUser: 'We could not identify your account for MFA verification. Please sign in again.',
        loginMfaSessionExpired: 'Your verification session expired. Please sign in again.',
        logoutError: 'Logout failed. Please try again.',
        logoutProgress: 'Logging out...',
        logoutSuccess: 'Logged out successfully',
        mfaCodeError: 'Invalid or expired code. Please try again.',
        mfaCodeHeading: 'Enter Verification Code',
        mfaCodeLabel: 'Code',
        mfaCodeRequired: 'Please enter the code',
        mfaInitiatedSuccess: 'MFA initiated successfully!',
        mfaExistingEnrollmentTitle: 'SMS MFA already enabled',
        mfaExistingEnrollmentDescription: 'We found a phone number on file. We will text your verification code there so you can finish signing in.',
        mfaExistingEnrollmentPending: 'Sending a verification code to your saved number...',
        mfaExistingEnrollmentSuccess: 'Code sent. Enter it below to continue.',
        mfaPhoneError: 'Failed to register phone number. Please try again.',
        mfaPhoneHeading: 'Set Up SMS MFA (Multi-Factor Authentication)',
        mfaPhoneRequired: 'Please enter a phone number (E.164 format)',
        mfaWhatsappHeading: 'Set Up WhatsApp MFA (Multi-Factor Authentication)',
        mfaPlaceholderCode: '123456',
        mfaSecretLabel: 'Secret (Optional)',
        mfaSecretPlaceholder: '(Leave blank if not needed)',
        mfaResendCodeButton: 'Resend code',
        mfaUseDifferentNumber: 'Use a different phone number',
        mfaUseSavedNumber: 'Keep using my saved number',
        mfaChangingNumberMessage: 'Using a new number will replace the phone currently on file for MFA.',
        mfaAutoRequestError: 'We could not send a code to the saved phone number. Please confirm your phone number again.',
        mfaVerifiedSuccess: 'MFA verified successfully!',
        registerAccountEmailRequired: 'Please enter your email',
        registerAccountFirstNameRequired: 'Please enter your first name',
        registerAccountHeading: 'Register an Account',
        registerAccountInvalidEmail: 'Invalid email format',
        registerAccountLastNameRequired: 'Please enter your last name',
        registerAccountPasswordComplex: 'Password must contain uppercase, lowercase, and a number',
        registerAccountPasswordMin: 'Minimum 8 characters',
        registerAccountPasswordRequired: 'Please enter a password',
        registerAccountPasswordHint: 'Use at least 8 characters with uppercase, lowercase, and a number. Re-enter to confirm.',
        registerAccountConfirmPasswordLabel: 'Confirm Password',
        registerAccountConfirmPasswordRequired: 'Please confirm your password',
        registerAccountConfirmPasswordMismatch: 'Passwords do not match',
        registerButton: 'Register',
        registerPhoneNumberError: 'Failed to register phone number. Please try again later.',
        registrationComplete: 'Registration Complete!',
        registrationCompleteDetail: 'You have successfully created your account and set up MFA.',
        registrationStepAccount: 'Account',
        registrationStepCode: 'Code',
        registrationStepDone: 'Done',
        registrationStepPhone: 'MFA',
        registrationStepSocial: 'Social',
        registrationSubmitError: 'Failed to submit registration details',
        rememberMeLabel: 'Keep me signed in',
        sendCodeButton: 'Send Code',
        signUpButton: 'Sign Up',
        verifyButton: 'Verify',
        registerWithGoogle: 'Register with Google',
        registerWithFacebook: 'Register with Facebook',
        continueWithEmail: 'Continue with Email',
        resetPasswordHeading: 'Choose a new password',
        resetPasswordDescription: 'Set a new password to regain access to your Tee Time account.',
        resetPasswordDisabledMessage: 'Password reset is currently unavailable. Please contact support.',
        resetPasswordRequirements: 'Use at least 8 characters with uppercase, lowercase, and a number; special characters are allowed.',
        resetPasswordMismatch: 'Passwords do not match',
        resetPasswordInvalidLink: 'This reset link is invalid or has expired. Request a new one.',
        resetPasswordSuccess: 'Your password has been updated successfully.',
        resetPasswordPasswordLabel: 'New password',
        resetPasswordConfirmLabel: 'Confirm password',
        resetPasswordSubmit: 'Update password',
        resetPasswordGoToLogin: 'Continue to login',
    },
    // South African English - same as en-US with minor spelling adjustments
    'en-ZA': {
        accountCreateFailed: 'Failed to create account. Please fix errors and try again.',
        accountCreatedSuccess: 'Account created successfully!',
        bookingBackHome: 'Back to Home',
        bookingSuccessHeading: 'Booking Successful',
        bookingSuccessText: 'Your tee time has been confirmed. A confirmation email will arrive shortly.',
        createAction: 'Create',
        createError: 'An error occurred while creating the price schedule. Please try again.',
        dontHaveAccount: "Don't have an account?",
        emailAlreadyExists: 'An account already exists for this email. Log in to continue with TeeTime, or reset your password to recover access.',
        emailAlreadyExistsLogin: 'Log in with this email',
        emailAlreadyExistsResetPassword: 'Reset password',
        existingIdentityConfirmation: 'We have you in our system. Please confirm this is you and complete your TeeTime registration.',
        existingIdentityConfirm: 'Confirm and continue',
        existingIdentityConfirmTitle: 'Confirm your existing identity',
        existingIdentityNextStep: 'You will sign in with this email, then TeeTime will finish linking player access.',
        forgotPassword: 'Forgot Password?',
        forgotPasswordHeading: 'Reset your password',
        forgotPasswordDescription: 'Enter the email address associated with your account and we will send you a reset link.',
        forgotPasswordDisabledMessage: 'Password reset is currently unavailable. Please contact support.',
        forgotPasswordSubmit: 'Send reset link',
        forgotPasswordSuccess: 'If the email exists, we have sent password reset instructions.',
        forgotPasswordInvalidEmail: 'Enter a valid email address',
        forgotPasswordEmailLabel: 'Email address',
        forgotPasswordEmailPlaceholder: 'you@example.com',
        forgotPasswordRemembered: 'Remembered your password?',
        genericError: 'An unexpected error occurred. Please try again.',
        goToHome: 'Go to Home',
        goToLogin: 'Go to Login',
        backButton: 'Back',
        invalidPhoneNumber: 'Phone number is invalid or not a mobile number.',
        invalidPhoneNumberField: 'Invalid phone number',
        loginButton: 'Log In',
        loginCheckError: 'Login failed. Check your email/password and try again.',
        loginError: 'Login failed. Please try again.',
        loginFailedTitle: 'Login Failed',
        loginHeading: 'Log In',
        loginSuccess: 'Login successful!',
        loginWelcome: 'Welcome to {appName}',
        loginMfaInstruction: 'Enter the verification code we just sent to finish signing in.',
        loginMfaMissingUser: 'We could not identify your account for MFA verification. Please sign in again.',
        loginMfaSessionExpired: 'Your verification session expired. Please sign in again.',
        logoutError: 'Logout failed. Please try again.',
        logoutProgress: 'Logging out...',
        logoutSuccess: 'Logged out successfully',
        mfaCodeError: 'Invalid or expired code. Please try again.',
        mfaCodeHeading: 'Enter Verification Code',
        mfaCodeLabel: 'Code',
        mfaCodeRequired: 'Please enter the code',
        mfaInitiatedSuccess: 'MFA initiated successfully!',
        mfaExistingEnrollmentTitle: 'SMS MFA already enabled',
        mfaExistingEnrollmentDescription: 'We found a phone number on file. We will text your verification code there so you can finish signing in.',
        mfaExistingEnrollmentPending: 'Sending a verification code to your saved number...',
        mfaExistingEnrollmentSuccess: 'Code sent. Enter it below to continue.',
        mfaPhoneError: 'Failed to register phone number. Please try again.',
        mfaPhoneHeading: 'Set Up SMS MFA (Multi-Factor Authentication)',
        mfaPhoneRequired: 'Please enter a phone number (E.164 format)',
        mfaWhatsappHeading: 'Set Up WhatsApp MFA (Multi-Factor Authentication)',
        mfaPlaceholderCode: '123456',
        mfaSecretLabel: 'Secret (Optional)',
        mfaSecretPlaceholder: '(Leave blank if not needed)',
        mfaResendCodeButton: 'Resend code',
        mfaUseDifferentNumber: 'Use a different phone number',
        mfaUseSavedNumber: 'Keep using my saved number',
        mfaChangingNumberMessage: 'Using a new number will replace the phone currently on file for MFA.',
        mfaAutoRequestError: 'We could not send a code to the saved phone number. Please confirm your phone number again.',
        mfaVerifiedSuccess: 'MFA verified successfully!',
        registerAccountEmailRequired: 'Please enter your email',
        registerAccountFirstNameRequired: 'Please enter your first name',
        registerAccountHeading: 'Register an Account',
        registerAccountInvalidEmail: 'Invalid email format',
        registerAccountLastNameRequired: 'Please enter your surname',
        registerAccountPasswordComplex: 'Password must contain uppercase, lowercase, and a number',
        registerAccountPasswordMin: 'Minimum 8 characters',
        registerAccountPasswordRequired: 'Please enter a password',
        registerAccountPasswordHint: 'Use at least 8 characters with uppercase, lowercase, and a number. Re-enter to confirm.',
        registerAccountConfirmPasswordLabel: 'Confirm Password',
        registerAccountConfirmPasswordRequired: 'Please confirm your password',
        registerAccountConfirmPasswordMismatch: 'Passwords do not match',
        registerButton: 'Register',
        registerPhoneNumberError: 'Failed to register phone number. Please try again later.',
        registrationComplete: 'Registration Complete!',
        registrationCompleteDetail: 'You have successfully created your account and set up MFA.',
        registrationStepAccount: 'Account',
        registrationStepCode: 'Code',
        registrationStepDone: 'Done',
        registrationStepPhone: 'MFA',
        registrationStepSocial: 'Social',
        registrationSubmitError: 'Failed to submit registration details',
        rememberMeLabel: 'Keep me signed in',
        sendCodeButton: 'Send Code',
        signUpButton: 'Sign Up',
        verifyButton: 'Verify',
        registerWithGoogle: 'Register with Google',
        registerWithFacebook: 'Register with Facebook',
        continueWithEmail: 'Continue with Email',
        resetPasswordHeading: 'Choose a new password',
        resetPasswordDescription: 'Set a new password to regain access to your Tee Time account.',
        resetPasswordDisabledMessage: 'Password reset is currently unavailable. Please contact support.',
        resetPasswordRequirements: 'Use at least 8 characters.',
        resetPasswordMismatch: 'Passwords do not match',
        resetPasswordInvalidLink: 'This reset link is invalid or has expired. Request a new one.',
        resetPasswordSuccess: 'Your password has been updated successfully.',
        resetPasswordPasswordLabel: 'New password',
        resetPasswordConfirmLabel: 'Confirm password',
        resetPasswordSubmit: 'Update password',
        resetPasswordGoToLogin: 'Continue to login',
    },
    // Afrikaans
    'af-ZA': {
        accountCreateFailed: 'Kon nie rekening skep nie. Maak asseblief foute reg en probeer weer.',
        accountCreatedSuccess: 'Rekening suksesvol geskep!',
        bookingBackHome: 'Terug na Tuis',
        bookingSuccessHeading: 'Bespreking Suksesvol',
        bookingSuccessText: "Jou afteetyd is bevestig. 'n Bevestigings-e-pos sal binnekort aankom.",
        createAction: 'Skep',
        createError: 'Fout tydens skepping van prysskedule. Probeer asseblief weer.',
        dontHaveAccount: "Het jy nie 'n rekening nie?",
        emailAlreadyExists: "Daar bestaan reeds 'n rekening vir hierdie e-pos. Teken aan om met TeeTime voort te gaan, of herstel jou wagwoord om toegang te herwin.",
        emailAlreadyExistsLogin: 'Teken aan met hierdie e-pos',
        emailAlreadyExistsResetPassword: 'Herstel wagwoord',
        existingIdentityConfirmation: 'Ons het jou in ons stelsel. Bevestig asseblief dat dit jy is en voltooi jou TeeTime-registrasie.',
        existingIdentityConfirm: 'Bevestig en gaan voort',
        existingIdentityConfirmTitle: 'Bevestig jou bestaande identiteit',
        existingIdentityNextStep: 'Jy sal met hierdie e-pos aanteken, en TeeTime sal daarna spelertoegang koppel.',
        forgotPassword: 'Wagwoord Vergeet?',
        forgotPasswordHeading: 'Herstel jou wagwoord',
        forgotPasswordDescription: "Voer die e-posadres in wat met jou rekening geassosieer word en ons sal vir jou 'n herstelskakel stuur.",
        forgotPasswordDisabledMessage: 'Wagwoordherstel is tans nie beskikbaar nie. Kontak asseblief ondersteuning.',
        forgotPasswordSubmit: 'Stuur herstelskakel',
        forgotPasswordSuccess: 'As die e-pos bestaan, het ons wagwoordherstelinstruksies gestuur.',
        forgotPasswordInvalidEmail: "Voer 'n geldige e-posadres in",
        forgotPasswordEmailLabel: 'E-posadres',
        forgotPasswordEmailPlaceholder: 'jy@voorbeeld.com',
        forgotPasswordRemembered: 'Onthou jy jou wagwoord?',
        genericError: 'Onverwagte fout het voorgekom. Probeer asseblief weer.',
        goToHome: 'Gaan na Tuis',
        goToLogin: 'Gaan na Aanteken',
        backButton: 'Terug',
        invalidPhoneNumber: "Telefoonnommer is ongeldig of nie 'n selfoon nie.",
        invalidPhoneNumberField: 'Ongeldige telefoonnommer',
        loginButton: 'Teken Aan',
        loginCheckError: 'Aanteken het misluk. Kontroleer jou e-pos/wagwoord en probeer weer.',
        loginError: 'Aanteken het misluk. Probeer asseblief weer.',
        loginFailedTitle: 'Aanteken Misluk',
        loginHeading: 'Teken Aan',
        loginSuccess: 'Suksesvol aangeteken!',
        loginWelcome: 'Welkom by {appName}',
        loginMfaInstruction: 'Voer die verifikasiekode in wat ons sopas gestuur het om aan te teken.',
        loginMfaMissingUser: 'Ons kon nie jou rekening vir MFA-verifikasie identifiseer nie. Teken asseblief weer aan.',
        loginMfaSessionExpired: 'Jou verifikasiesessie het verval. Teken asseblief weer aan.',
        logoutError: 'Afmelding het misluk. Probeer asseblief weer.',
        logoutProgress: 'Meld tans af...',
        logoutSuccess: 'Suksesvol afgemeld',
        mfaCodeError: 'Ongeldige of verstrykte kode. Probeer asseblief weer.',
        mfaCodeHeading: 'Voer Verifikasiekode In',
        mfaCodeLabel: 'Kode',
        mfaCodeRequired: 'Voer asseblief die kode in',
        mfaInitiatedSuccess: 'MFA suksesvol geïnisieer!',
        mfaExistingEnrollmentTitle: 'SMS MFA reeds geaktiveer',
        mfaExistingEnrollmentDescription: "Ons het 'n telefoonnommer op lêer gevind. Ons sal jou verifikasiekode daarheen stuur sodat jy kan aanteken.",
        mfaExistingEnrollmentPending: "Stuur tans 'n verifikasiekode na jou gestoorde nommer...",
        mfaExistingEnrollmentSuccess: 'Kode gestuur. Voer dit hieronder in om voort te gaan.',
        mfaPhoneError: 'Kon nie telefoonnommer registreer nie. Probeer asseblief weer.',
        mfaPhoneHeading: 'Stel SMS MFA Op (Multi-Faktor Verifikasie)',
        mfaPhoneRequired: "Voer asseblief 'n telefoonnommer in (E.164 formaat)",
        mfaWhatsappHeading: 'Stel WhatsApp MFA Op (Multi-Faktor Verifikasie)',
        mfaPlaceholderCode: '123456',
        mfaSecretLabel: 'Geheim (Opsioneel)',
        mfaSecretPlaceholder: '(Los leeg indien nie nodig nie)',
        mfaResendCodeButton: 'Herstuur kode',
        mfaUseDifferentNumber: "Gebruik 'n ander telefoonnommer",
        mfaUseSavedNumber: 'Hou aan met my gestoorde nommer',
        mfaChangingNumberMessage: "Gebruik van 'n nuwe nommer sal die huidige telefoonnommer op lêer vir MFA vervang.",
        mfaAutoRequestError: "Ons kon nie 'n kode na die gestoorde telefoonnommer stuur nie. Bevestig asseblief jou telefoonnommer weer.",
        mfaVerifiedSuccess: 'MFA suksesvol geverifieer!',
        registerAccountEmailRequired: 'Voer asseblief jou e-pos in',
        registerAccountFirstNameRequired: 'Voer asseblief jou voornaam in',
        registerAccountHeading: "Registreer 'n Rekening",
        registerAccountInvalidEmail: 'Ongeldige e-posformaat',
        registerAccountLastNameRequired: 'Voer asseblief jou van in',
        registerAccountPasswordComplex: "Wagwoord moet hoofletters, kleinletters en 'n nommer bevat",
        registerAccountPasswordMin: 'Minimum 8 karakters',
        registerAccountPasswordRequired: "Voer asseblief 'n wagwoord in",
        registerAccountPasswordHint: "Gebruik ten minste 8 karakters met hoofletters, kleinletters en 'n nommer. Voer weer in om te bevestig.",
        registerAccountConfirmPasswordLabel: 'Bevestig Wagwoord',
        registerAccountConfirmPasswordRequired: 'Bevestig asseblief jou wagwoord',
        registerAccountConfirmPasswordMismatch: 'Wagwoorde stem nie ooreen nie',
        registerButton: 'Registreer',
        registerPhoneNumberError: 'Kon nie telefoonnommer registreer nie. Probeer asseblief later weer.',
        registrationComplete: 'Registrasie Voltooi!',
        registrationCompleteDetail: 'Jy het jou rekening suksesvol geskep en MFA opgestel.',
        registrationStepAccount: 'Rekening',
        registrationStepCode: 'Kode',
        registrationStepDone: 'Klaar',
        registrationStepPhone: 'MFA',
        registrationStepSocial: 'Sosiaal',
        registrationSubmitError: 'Kon nie registrasiebesonderhede indien nie',
        rememberMeLabel: 'Hou my aangemeld',
        sendCodeButton: 'Stuur Kode',
        signUpButton: 'Registreer',
        verifyButton: 'Verifieer',
        registerWithGoogle: 'Registreer met Google',
        registerWithFacebook: 'Registreer met Facebook',
        continueWithEmail: 'Gaan voort met E-pos',
        resetPasswordHeading: "Kies 'n nuwe wagwoord",
        resetPasswordDescription: "Stel 'n nuwe wagwoord in om toegang tot jou Tee Time rekening te herwin.",
        resetPasswordDisabledMessage: 'Wagwoordherstel is tans nie beskikbaar nie. Kontak asseblief ondersteuning.',
        resetPasswordRequirements: 'Gebruik ten minste 8 karakters.',
        resetPasswordMismatch: 'Wagwoorde stem nie ooreen nie',
        resetPasswordInvalidLink: "Hierdie herstelskakel is ongeldig of het verval. Versoek 'n nuwe een.",
        resetPasswordSuccess: 'Jou wagwoord is suksesvol opgedateer.',
        resetPasswordPasswordLabel: 'Nuwe wagwoord',
        resetPasswordConfirmLabel: 'Bevestig wagwoord',
        resetPasswordSubmit: 'Dateer wagwoord op',
        resetPasswordGoToLogin: 'Gaan voort na aanteken',
    },
};
export function getMessage(locale, key) {
    const catalogue = messages[locale] ?? messages['en-US'];
    return catalogue[key];
}
//# sourceMappingURL=messages.js.map